import { defineMessages, type IntlShape } from "react-intl";
import {
  BRAND_REAL_PEOPLE_REFERENCE_POLICIES,
  isPhotographicBrandReferenceClassification,
  type BrandRealPeopleReferencePolicy,
} from "@runcheetah/shared";

const mediaGalleryMessages = defineMessages({
  instagramSourceLink: {
    id: "mediaGallery.sourceLink.instagram",
    defaultMessage: "View Instagram post",
  },
  websiteSourceLink: {
    id: "mediaGallery.sourceLink.website",
    defaultMessage: "View source page",
  },
  peoplePolicyStyleOnly: {
    id: "mediaGallery.peoplePolicy.styleOnly",
    defaultMessage: "Style and setting only",
  },
  peoplePolicyAllowPeople: {
    id: "mediaGallery.peoplePolicy.allowPeople",
    defaultMessage: "Allow people",
  },
});

export const realPeopleReferencePolicies = [...BRAND_REAL_PEOPLE_REFERENCE_POLICIES];
export type RealPeopleReferencePolicy = BrandRealPeopleReferencePolicy;

export const getMediaGallerySourceLinkLabel = (
  sourceType: "website" | "instagram",
  intl: IntlShape,
): string =>
  intl.formatMessage(
    sourceType === "instagram"
      ? mediaGalleryMessages.instagramSourceLink
      : mediaGalleryMessages.websiteSourceLink,
  );

export const getRealPeopleReferencePolicyLabel = (
  policy: RealPeopleReferencePolicy,
  intl: IntlShape,
): string =>
  intl.formatMessage(
    policy === "allow_people"
      ? mediaGalleryMessages.peoplePolicyAllowPeople
      : mediaGalleryMessages.peoplePolicyStyleOnly,
  );

export const formatPeopleDetectionConfidence = (confidencePct: number | null): string =>
  confidencePct === null ? "" : `${confidencePct}%`;

export type MediaGalleryRealPeopleDetectionStatus =
  | "detected"
  | "not_detected"
  | "below_threshold"
  | "pending"
  | "unknown";
export type MediaGalleryUploadProcessingStatus = "queued" | "processing" | "done" | "failed";

export const resolveMediaGalleryRealPeopleReferencePolicyApplies = (input: {
  referenceEnabled: boolean;
  imageClassification: string | null;
  realPeopleDetectionStatus: MediaGalleryRealPeopleDetectionStatus;
}): boolean => {
  if (!input.referenceEnabled) {
    return false;
  }

  if (input.realPeopleDetectionStatus === "detected") {
    return true;
  }

  return (
    (input.realPeopleDetectionStatus === "pending" ||
      input.realPeopleDetectionStatus === "unknown") &&
    isPhotographicBrandReferenceClassification(input.imageClassification)
  );
};

export const isMediaGalleryUploadProcessing = (asset: {
  width: number | null;
  height: number | null;
  imageClassification: string | null;
  realPeopleDetectionStatus: MediaGalleryRealPeopleDetectionStatus;
  uploadProcessingStatus?: MediaGalleryUploadProcessingStatus | null;
  sources: Array<{ type: string }>;
}): boolean =>
  asset.sources.some((source) => source.type === "upload") &&
  asset.uploadProcessingStatus !== "failed" &&
  (asset.width === null ||
    asset.height === null ||
    asset.imageClassification === null ||
    asset.realPeopleDetectionStatus === "pending") &&
  asset.uploadProcessingStatus !== "done";
